/**
 * Kenecting's PRODUCT IDENTITY: the name, the apex it answers on, and the few sentences its shell says -- what the
 * Kenecting application (`apps/kenecting`, the Connect desk's own host, operator decision 2026-09-18) shows, and the
 * name the desk it hosts (`spa/connect`) reads for its title and its own copy.
 *
 * AN OVERLAY ON THE ACTIVE BRAND, NOT A SECOND BRAND, the same tier `const/noor-identity.ts` established: the session
 * cookie, the sign-in modal and every shared component read `ACTIVE_BRAND`; Kenecting is a desk hosted under it with
 * its own face -- staff's alone until 2026-09-18, any signed-in viewer's since, scoped to the groups they belong to.
 * Unlike Noor's leaf this one DOES carry a hostname, and that is not the brand tier arriving early:
 * `kenecting.com` is where the desk answers, not a company on record -- `BrandIdentity` wants a legal name, a wire
 * address and a payout account, and a desk has none of those to give. The apex lives here because
 * this product answers on its own rather than under the active brand's; the GRAMMAR is the fleet's, read from the one
 * composer (`apexHostnames` / `httpsOrigins`, `const/brand-hostnames.ts`) over this apex, so nothing here re-spells it.
 *
 * NO WORDMARK ART. The face on screen is the desk's own title bar (`CONNECT_COPY.appTitle`, which has said "Kenecting"
 * since 2026-08-14) and the site icon (`apps/kenecting/src/app/icon.svg`); the app draws no bar of its own.
 */

import { logInTo } from './label-values.ts';
import { wranglerEnvKeyFor } from './config-environments.ts';
import { apexHostnames, httpsOrigins } from './brand-hostnames.ts';
import type { WranglerEnvKey } from './config-environments.ts';

export const KENECTING_PRODUCT_NAME = 'Kenecting';

/** The product's own apex -- a zone on the fleet's Cloudflare account, which the app's manifest attaches as each env's
 *  custom domain at deploy. */
export const KENECTING_APEX = 'kenecting.com';

/** The HOSTNAME the desk answers on, per wrangler env block: the fleet's host-app grammar over this apex, stated once
 *  on {@link apexHostnames} with what the manifest does with the answer. */
export const KENECTING_APP_HOSTNAMES: Readonly<Record<WranglerEnvKey, string>> = apexHostnames(KENECTING_APEX, null);

/** The public ORIGIN the app answers on, per wrangler env block -- what its manifest binds as `NEXT_PUBLIC_APP_URL`
 *  and what the admin console links out to: the scheme over the hostname above, never a second spelling of it. */
export const KENECTING_APP_URLS: Readonly<Record<WranglerEnvKey, string>> = httpsOrigins(KENECTING_APP_HOSTNAMES);

/**
 * The origin a page on the fleet links out to, by the environment it is running in.
 * @runtime-derived-ssot-helper indexes {@link KENECTING_APP_URLS} by `wranglerEnvKeyFor`; no second spelling of either
 * origin or of the choice between them.
 */
export function kenectingAppUrl(isProduction: boolean): string {
	return KENECTING_APP_URLS[wranglerEnvKeyFor(isProduction)];
}

/**
 * Every sentence the Kenecting SHELL says. The desk's own words stay in `const/connect-copy.ts`; these
 * are the sentences a person reads BEFORE the desk is on screen, and the app's gate (`SignInGate`) reads them.
 */
export const KENECTING_APP_COPY = {
	/** What the product is for, in the desk's own vocabulary (no risk, tier, signal or escalation: `connect-copy.ts`).
	 *  One line, so it sits under the sign-in modal's title without wrapping. */
	purpose: 'Answer the people writing and calling in.',
	/** The gate's heading: the act, in the verb the button under it carries.
	 *
	 *  IT IS THE ONLY REFUSAL-SHAPED SENTENCE LEFT, and that is the whole of the door now. The not-authorized line that
	 *  stood beside it is gone with the roster arm that spoke it (operator, 2026-09-18): any signed-in viewer reaches
	 *  the desk, what they find on it is decided per flow at the connect worker's gate, and a viewer with no group is
	 *  met by the create-your-first-group wizard rather than by a sentence telling them they do not belong. */
	signedOutTitle: logInTo(KENECTING_PRODUCT_NAME),
} as const;

// ROUTES

/** The Kenecting app's pages beyond the desk at `/`: the paths its Next router serves off `apps/kenecting/src/app`, and
 *  the paths the desk it hosts (`spa/connect`) opens. `GROUPS` is the groups surface and `PROFILE` the person's profile
 *  (2026-09-26, off the hub and onto this host), each mounted whole behind the desk's own sign-in. */
export const KENECTING_APP_ROUTES = {
	GROUPS: '/groups',
	PROFILE: '/profile',
} as const;
